import { __assign, __read } from "tslib";
import React, { useState, useRef } from 'react';
import { generateBars } from './utils/chart.utils';
import { getSelectors } from './utils/tooltip.utils';
import { Bars } from './components';
import { ChartBase, ChartTooltip, Grid, Axes, ZeroIntersection, DistributedChartTooltip as BarChartTooltip, } from '../../components';
import { useDynamicChartLayout } from '../../hooks';
import { theme as defaultTheme } from '../../theme';
import { DEFAULT_MARGINS, MAX_TOOLTIP_WIDTH_FACTOR } from './constants';
import { TOOLTIP_HIDE_TIME } from '../../constants';
export var BarChart = function (_a) {
    var data = _a.data, svgDimensions = _a.svgDimensions, labelSelector = _a.labelSelector, _b = _a.theme, theme = _b === void 0 ? defaultTheme : _b, _c = _a.margins, margins = _c === void 0 ? DEFAULT_MARGINS : _c, _d = _a.layout, layout = _d === void 0 ? 'vertical' : _d, _f = _a.useDynamicLayout, useDynamicLayout = _f === void 0 ? true : _f, _g = _a.minValue, minValue = _g === void 0 ? 'auto' : _g, _h = _a.maxValue, maxValue = _h === void 0 ? 'auto' : _h, _j = _a.keys, keys = _j === void 0 ? ['value'] : _j, _k = _a.disabledKeys, disabledKeys = _k === void 0 ? [] : _k, _l = _a.stackMode, stackMode = _l === void 0 ? 'normal' : _l, _m = _a.groupMode, groupMode = _m === void 0 ? 'grouped' : _m, _o = _a.xScaleSettings, xScaleSettings = _o === void 0 ? { type: 'band' } : _o, _p = _a.yScaleSettings, yScaleSettings = _p === void 0 ? { type: 'linear' } : _p, _q = _a.barPadding, barPadding = _q === void 0 ? 0.1 : _q, _r = _a.showValues, showValues = _r === void 0 ? false : _r, _s = _a.valuesAutocolor, valuesAutocolor = _s === void 0 ? true : _s, barsOrder = _a.barsOrder, xAxisTitle = _a.xAxisTitle, yAxisTitle = _a.yAxisTitle, _t = _a.tooltipSettings, tooltipSettings = _t === void 0 ? {} : _t, _u = _a.activeKey, activeKey = _u === void 0 ? null : _u, dataSeriesOffset = _a.dataSeriesOffset;
    var svgElement = useRef(null);
    var _v = useDynamicChartLayout(useDynamicLayout, margins), layoutMargins = _v.layoutMargins, layoutReady = _v.layoutReady, setLayoutReady = _v.setLayoutReady, setLayoutMargins = _v.setLayoutMargins;
    var _w = generateBars({
        data: data,
        margins: layoutMargins,
        dimension: svgDimensions,
        labelSelector: labelSelector,
        barPadding: barPadding,
        barsOrder: barsOrder,
        layout: layout,
        keys: keys,
        disabledKeys: disabledKeys,
        minValue: minValue,
        maxValue: maxValue,
        colors: theme.colors,
        stackMode: stackMode,
        groupMode: groupMode,
        xScaleSettings: xScaleSettings,
        yScaleSettings: yScaleSettings,
        xAxisTitle: xAxisTitle,
        yAxisTitle: yAxisTitle,
        dataSeriesOffset: dataSeriesOffset,
    }), bars = _w.bars, xScale = _w.xScale, yScale = _w.yScale, localizedData = _w.localizedData, settings = _w.settings, _x = _w.settings, xTitle = _x.xAxisTitle, yTitle = _x.yAxisTitle;
    var themeTooltipsSettings = theme.tooltip;
    var clearTooltip = useRef(null);
    var _y = __read(useState({
        selectors: null,
        visible: false,
        x: 0,
        y: 0,
    }), 2), tooltip = _y[0], setTooltip = _y[1];
    return (React.createElement(React.Fragment, null,
        React.createElement(ChartBase, __assign({ ref: svgElement, theme: theme, svgDimensions: svgDimensions, margins: layoutMargins }, settings),
            React.createElement(Axes, { layout: layout, svgElement: svgElement, useDynamicLayout: useDynamicLayout, initialMargins: margins, onComputeLayout: function (margins) {
                    setLayoutMargins(margins);
                    setLayoutReady(true);
                }, xScale: xScale, yScale: yScale, xTitle: xTitle, yTitle: yTitle }),
            layoutReady && (React.createElement(React.Fragment, null,
                React.createElement(Grid, { xScale: xScale, yScale: yScale }),
                React.createElement(ZeroIntersection, { xScale: xScale, yScale: yScale, layout: layout }),
                React.createElement(Bars, { bars: bars, activeKey: activeKey, stackMode: stackMode, groupMode: groupMode, layout: layout, showValues: showValues, valuesAutocolor: valuesAutocolor, onBarMouseEnter: function (_e, _key, selector, _a) {
                        var x = _a.x, y = _a.y;
                        if (clearTooltip.current)
                            clearTimeout(clearTooltip.current);
                        if (themeTooltipsSettings.enabled) {
                            var selectors = getSelectors({
                                groupMode: groupMode,
                                keys: keys,
                                disabledKeys: disabledKeys,
                                colors: theme.colors,
                                selector: selector,
                            });
                            setTooltip({ visible: true, x: x, y: y, selectors: selectors });
                        }
                    }, onBarMouseLeave: function () {
                        if (themeTooltipsSettings.enabled) {
                            clearTooltip.current = setTimeout(function () {
                                setTooltip({
                                    selectors: null,
                                    visible: false,
                                    x: 0,
                                    y: 0,
                                });
                            }, TOOLTIP_HIDE_TIME);
                        }
                    }, dataSeriesOffset: dataSeriesOffset }),
                React.createElement(ChartTooltip, { visible: tooltip.visible, x: tooltip.x, y: tooltip.y, hasSpacing: false }, tooltip.selectors && (React.createElement(BarChartTooltip, { data: localizedData, keys: keys, disabledKeys: disabledKeys, isPercentage: stackMode === 'percent', isStacked: groupMode === 'stacked', selectors: tooltip.selectors, tooltipSettings: tooltipSettings, labelSelector: labelSelector, maxWidth: MAX_TOOLTIP_WIDTH_FACTOR * svgDimensions.width, isTimePrecise: (xScaleSettings === null || xScaleSettings === void 0 ? void 0 : xScaleSettings.type) === 'time' }))))))));
};
export default BarChart;
